"use client";

import { useGSAP } from "@gsap/react";
import clsx from "clsx";
import gsap from "gsap";
import { useLenis } from "lenis/react";
import { usePathname } from "next/navigation";
import { useState, useEffect, useRef, memo } from "react";
import { useApp } from "@/context/AppContext";
import NavLink from "./nav-link";
import Cta from "../cta/cta";
import Close from "../icon/close";
import Logo from "../svg/logo";

function MobileNavOverlay() {
  const lenis = useLenis();
  const pathname = usePathname();
  const [activeLink, setActiveLink] = useState(pathname);
  const [hoveredLink, setHoveredLink] = useState(null);
  const isClickDisabledRef = useRef(false);
  const darkBgRef = useRef(null);

  const { isRouteChanging, isMobile, isMobileNavOpen, setIsMobileNavOpen, navLinks, underlineSize } = useApp();

  const bgRef = useRef(null);
  const closeBtnRef = useRef(null);
  const logoRef = useRef(null);
  const linksRefs = useRef([]);
  const ctaRef = useRef([]);

  const handleLinkClick = (href) => {
    setActiveLink(href);
    isClickDisabledRef.current = true;
  };

  const handleHoverChange = (href, isHovering) => {
    if (!isClickDisabledRef.current) {
      setHoveredLink(isHovering ? href : null);
    }
  };

  useEffect(() => {
    if (navLinks.find(n => n.href === pathname)) {
      setActiveLink(pathname);
    } else if (pathname.includes('/record-gallery)')) {
      setActiveLink(navLinks.find(n => n.label === 'Record Gallery').href);
    }
  }, [pathname])

  const animateIn = () => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.killTweensOf([darkBgRef.current, bgRef.current, closeBtnRef.current, logoRef.current, ...linksRefs.current, ctaRef.current])
      gsap.to(darkBgRef.current, {
        opacity: 1
      })
      gsap.to(bgRef.current, {
        scaleY: 1,
        ease: 'expo.out',
        duration: 1.2
      })


      gsap.to([closeBtnRef.current, logoRef.current, ...linksRefs.current, ctaRef.current], {
        opacity: 1,
        stagger: 0.1
      })

      gsap.to([closeBtnRef.current, logoRef.current, ...linksRefs.current, ctaRef.current], {
        y: 0,
        duration: 1.2,
        ease: 'expo.out',
        stagger: 0.1,
      })
    })
  }

  const animateOut = () => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.killTweensOf([darkBgRef.current, bgRef.current, closeBtnRef.current, logoRef.current, ...linksRefs.current, ctaRef.current])
      gsap.to([closeBtnRef.current, logoRef.current, ...linksRefs.current, ctaRef.current], {
        opacity: 0,
      })

      gsap.to([darkBgRef.current], {
        opacity: 0,
        delay: 0.2
      })

      gsap.to(bgRef.current, {
        scaleY: 0,
        ease: 'expo.out',
        delay: 0.2,
        duration: 1,
        onComplete: () => {
          gsap.set([bgRef.current, closeBtnRef.current, logoRef.current, ...linksRefs.current, ctaRef.current], { clearProps: 'all' })
          setIsMobileNavOpen(false);
        }
      })
    })
  }

  const close = () => {
    animateOut()
  }

  useEffect(() => {
    if (isMobileNavOpen) {
      animateIn();
    }
  }, [isMobileNavOpen])

  useEffect(() => {
    if (isMobileNavOpen) {
      lenis?.stop();
    } else {
      lenis?.start();
    }

    return () => lenis?.start();
  }, [isMobileNavOpen, lenis]);

  useEffect(() => {
    if (isRouteChanging && isMobileNavOpen) {
      close();
    }
  }, [isRouteChanging, isMobileNavOpen])

  useEffect(() => {
    if (!isMobile) {
      const parts = pathname.split('/');
      const normalizedPath = parts.length > 3 ? parts.slice(0, 3).join('/') : pathname;
      setActiveLink(normalizedPath)
    }
  }, [pathname, isMobile])

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([closeBtnRef.current, logoRef.current, ...linksRefs.current, ctaRef.current], {
        opacity: 1,
        y: 0
      })
    })
  })

  return (
    <div className={clsx(
      "nav-overlay fixed top-0 left-0 z-3000 w-full full-height-screen !bg-transparent",
      isMobileNavOpen ? 'pointer-events-auto' : 'pointer-events-none hidden'
    )}>
      <div ref={darkBgRef} className="absolute top-0 left-0 w-full h-full bg-black/50 opacity-0" />
      <div ref={bgRef} className="scale-y-0 absolute origin-top top-0 left-0 w-full h-full bg-theme will-change-transform" />
      <div className="py-(--space-5) gap-(--space-10) flex flex-col items-center justify-center w-full h-[100svh]">
        <Cta ref={closeBtnRef} className="opacity-0 translate-y-[-100%] cta-circle-small cta-theme-reverse absolute top-(--space-5) right-(--space-5) will-change-transform" onClick={close}><Close /></Cta>

        <a ref={logoRef} aria-label="Ancestry home" href="https://www.ancestry.com/" className="relative opacity-0 translate-y-[-100%] pt-3 mb-auto will-change-transform">
          <Logo className="relative" />
        </a>

        {navLinks.map(({ label, href, underlineSize, overlayUnderlineScale }, index) => (
          <NavLink
            className={'opacity-0 translate-y-[-100%] relative will-change-transform'}
            ref={(el) => linksRefs.current[index] = el}
            key={href}
            label={label}
            href={href}
            underlineScale={overlayUnderlineScale}
            underlineSize={underlineSize}
            isActive={activeLink === href && (!hoveredLink || hoveredLink === href)}
            onHoverChange={(isHovering) => handleHoverChange(href, isHovering)}
            onClick={() => handleLinkClick(href)}
          />
        ))}
        <Cta ref={ctaRef} href="https://www.ancestry.com/offers/freetrial" className="mt-auto relative opacity-0 translate-y-[-100%] will-change-transform cta-big">Start a free trial</Cta>
      </div>
    </div>
  )
}

export default memo(MobileNavOverlay)
